// Vendor-neutral marketing analytics helper.
//
// Safely pushes structured events to window.dataLayer when available so a
// GTM / GA4 / Meta / Clarity container (if one is later wired via env) can
// consume them. The site works perfectly with tracking disabled — nothing
// here ever throws or blocks the UI. NO form-entered personal data is sent.

import { getUtms } from "./waTracker";

// Optional container id integration point. IDs are validated at build/run
// time; when absent or malformed the integration stays fully inactive (we
// only keep the harmless dataLayer queue). No IDs are invented.
const GTM_RE = /^GTM-[A-Z0-9]{4,10}$/;
const GA4_RE = /^G-[A-Z0-9]{4,14}$/;
const _rawGtm = process.env.REACT_APP_GTM_ID || "";
const _rawGa4 = process.env.REACT_APP_GA4_ID || "";
export const GTM_ID = GTM_RE.test(_rawGtm.trim()) ? _rawGtm.trim() : null;
export const GA4_ID = GA4_RE.test(_rawGa4.trim()) ? _rawGa4.trim() : null;
export const ANALYTICS_ACTIVE = Boolean(GTM_ID || GA4_ID);

// Bootstrap the vendor container ONLY when a valid id is supplied.
export function initAnalytics() {
  try {
    if (typeof window === "undefined" || window.__tf_analytics_loaded) return;
    if (GTM_ID) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
      document.head.appendChild(s);
      window.__tf_analytics_loaded = true;
    } else if (GA4_ID) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", GA4_ID, { send_page_view: true });
      window.__tf_analytics_loaded = true;
    }
  } catch { /* analytics must never break the site */ }
}

// Ensure the queue exists as soon as this module is evaluated (belt-and-suspenders
// alongside the synchronous init in index.html) — never gated on a vendor id.
if (typeof window !== "undefined") {
  window.dataLayer = window.dataLayer || [];
}

/**
 * Push a structured marketing event to the dataLayer.
 * @param {string} event - event name (e.g. "form_submit_success")
 * @param {object} params - extra, non-PII context
 */
export function trackEvent(event, params = {}) {
  try {
    if (typeof window === "undefined") return;
    // Guarantee the queue exists BEFORE anything else can throw (no GTM required).
    window.dataLayer = window.dataLayer || [];
    const utm = getUtms();
    window.dataLayer.push({
      event,
      page_path: window.location.pathname,
      page_title: (document.title || "").slice(0, 200),
      ...(utm && Object.keys(utm).length ? { ...utm } : {}),
      ...params,
    });
  } catch {
    /* analytics must never break the site */
  }
}

// Convenience wrappers for the instrumented public-site events.
// Conversion events are deduped per page load: identical success events for
// the same form/service fire once, and only after a successful backend response.
const _firedConversions = new Set();
function trackConversionOnce(event, ctx = {}) {
  const key = `${event}:${ctx.form || ""}:${ctx.service || ""}:${ctx.source_page || ""}`;
  if (_firedConversions.has(key)) return;
  _firedConversions.add(key);
  trackEvent(event, ctx);
}

export const track = {
  whatsappClick: (location) => trackEvent("whatsapp_click", { cta_location: location }),
  phoneClick: (location) => trackEvent("phone_click", { cta_location: location }),
  formStart: (ctx) => trackEvent("form_start", ctx),
  formStepComplete: (step, ctx) => trackEvent("form_step_complete", { step, ...ctx }),
  formSubmitSuccess: (ctx) => trackConversionOnce("form_submit_success", ctx),
  formSubmitError: (ctx) => trackEvent("form_submit_error", ctx),
  siteVisitCta: (location) => trackEvent("site_visit_cta", { cta_location: location }),
  portfolioView: (ctx) => trackEvent("portfolio_view", ctx),
  thankYouView: (ctx) => trackEvent("thank_you_view", ctx),
  // Fit-Out Budget Planner (calculator) — non-PII only.
  calculatorStart: (ctx) => trackEvent("calculator_start", ctx),
  calculatorStepComplete: (step, ctx) => trackEvent("calculator_step_complete", { step, ...ctx }),
  calculatorResult: (ctx) => trackEvent("calculator_result", ctx),
  calculatorLeadSubmit: (ctx) => trackEvent("calculator_lead_submit", ctx),
  calculatorWhatsappClick: (ctx) => trackEvent("calculator_whatsapp_click", ctx),
};
